import { ArrowRight, Calendar, Clock, User } from "lucide-react";
import type { Schedule } from "../../api/schedule";
import { DailyScheduleSkeleton } from "../ui/skeleton";
import { formatTimeIST } from "../../utils/function";

interface DailyScheduleProps {
  scheduleItems: Schedule[];
  isLoading: boolean;
}

export default function DailySchedule({ scheduleItems, isLoading }: DailyScheduleProps) {

  return (
    <section className="py-24 pt-32 px-4 bg-black relative">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-white text-3xl font-medium mb-4">Daily Schedule</h2>
          <p className="text-white/60">Catch your favorite shows throughout the day. All times in EST.</p>
        </div>

        {isLoading ? (
          <DailyScheduleSkeleton />
        ) : scheduleItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scheduleItems.map((item) => (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-10 h-10 rounded-lg bg-linear-to-b ${item.gradient} flex items-center justify-center shrink-0 shadow-lg`}>
                    <Clock className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-cyan-100 text-sm font-medium mb-1">
                      {formatTimeIST(item.startTime)} - {formatTimeIST(item.endTime)}
                    </p>
                    <h3 className="text-white text-lg font-medium">{item.show.name}</h3>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <User className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                  <p className="text-white/80">{item.show.hostName}</p>
                </div>

                <p className="text-white/60 text-sm leading-relaxed">
                  {item.show.description}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="col-span-full text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-4">
              <Calendar className="w-8 h-8 text-white/50" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">No Shows Scheduled</h3>
            <p className="text-white/60 max-w-md mx-auto">
              There are no shows scheduled for today. Please check back later or view our upcoming schedule.
            </p>
          </div>
        )}

        {scheduleItems.length > 6 && (
          <div className="mt-12 flex justify-center">
            <button className="flex items-center gap-2 bg-linear-to-b from-[#174868] to-[#147b89] text-white px-6 py-3 rounded-lg shadow-lg shadow-cyan-900/30 hover:scale-105 transition-transform">
              <span>View All Schedules</span>
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}